export type MarkupPlatform = 'tiktok' | 'instagram' | 'youtube';

const ALL_MARKUP_PLATFORMS: MarkupPlatform[] = ['tiktok', 'instagram', 'youtube'];

/**
 * Which platform blocks the "required markup" card should render. Clippers
 * must not be shown (or asked to copy tags for) a platform the campaign's
 * `allowed_platforms` objective rule excludes — that platform's submissions
 * get `platform_not_allowed` regardless of hashtags, so promising it here
 * would be actively misleading.
 *
 * `null`/empty (unset) means every platform is allowed, matching the
 * backend's own null-or-empty-means-all-allowed contract.
 */
export function resolveVisibleMarkupPlatforms(allowedPlatforms: string[] | null | undefined): MarkupPlatform[];
export function resolveVisibleMarkupPlatforms(allowedPlatforms: string[] | null | undefined, includeFacebook: boolean): Array<MarkupPlatform | 'facebook'>;
export function resolveVisibleMarkupPlatforms(
  allowedPlatforms: string[] | null | undefined, includeFacebook = false,
): Array<MarkupPlatform | 'facebook'> {
  const supported: Array<MarkupPlatform | 'facebook'> = includeFacebook ? [...ALL_MARKUP_PLATFORMS, 'facebook'] : ALL_MARKUP_PLATFORMS;
  if (!Array.isArray(allowedPlatforms) || allowedPlatforms.length === 0) {
    return supported;
  }
  const normalized = new Set(allowedPlatforms.map((p) => String(p).toLowerCase()));
  return supported.filter((p) => normalized.has(p));
}

/**
 * Builds the text shown in (and copied from) a single platform's required-markup
 * box: the platform's required hashtags plus the campaign's required mention,
 * so a clipper who copies the box gets everything the objective rules check for.
 */
export function buildRequiredMarkupCopyText({
  hashtags,
  mention,
}: {
  hashtags?: string | null;
  mention?: string | null;
}): string {
  const parts = [hashtags, mention]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0);
  return parts.join(' ');
}
